import React, { useRef, useState } from "react";
import { Paperclip, X, FileText, Image as ImageIcon, DownloadSimple, SpinnerGap } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { formatBytes, isImageType } from "../lib/utils";

/**
 * Uploader used on the New Ticket form. Uploads each picked file straight to
 * Bunny CDN (via the backend) and reports the resulting attachment objects up
 * through `onChange`. `value` is an array of { name, size, content_type, path, url }.
 */
export function AttachmentUploader({ value, onChange, maxMb = 25 }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState([]); // names currently uploading
  const [error, setError] = useState("");

  const pick = () => inputRef.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-picking same file
    setError("");
    for (const f of files) {
      if (f.size > maxMb * 1024 * 1024) {
        setError(`${f.name} exceeds the ${maxMb} MB limit.`);
        continue;
      }
      setUploading((u) => [...u, f.name]);
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        onChange((prev) => [...prev, data]);
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || `Failed to upload ${f.name}`);
      } finally {
        setUploading((u) => u.filter((n) => n !== f.name));
      }
    }
  };

  const remove = async (att) => {
    onChange((prev) => prev.filter((a) => a.path !== att.path));
    api.delete(`/uploads`, { params: { path: att.path } }).catch(() => {});
  };

  return (
    <div className="space-y-2" data-testid="attachment-uploader">
      <input ref={inputRef} type="file" multiple className="hidden" onChange={onFiles}
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.odt,.zip" data-testid="attachment-input" />
      <button type="button" onClick={pick}
        className="w-full border border-dashed border-slate-300 rounded-md py-3 text-sm text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors inline-flex items-center justify-center gap-2">
        <Paperclip size={15} /> Attach images or documents
      </button>

      {(value.length > 0 || uploading.length > 0) && (
        <div className="space-y-1.5">
          {value.map((a) => (
            <div key={a.path} className="flex items-center gap-2.5 border border-slate-200 rounded-md px-2.5 py-1.5 bg-white" data-testid="attachment-item">
              <AttachIcon att={a} />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-700 truncate">{a.name}</div>
                <div className="text-[10px] text-slate-400">{formatBytes(a.size)}</div>
              </div>
              <button type="button" onClick={() => remove(a)} className="text-slate-400 hover:text-rose-600" title="Remove">
                <X size={14} />
              </button>
            </div>
          ))}
          {uploading.map((n) => (
            <div key={n} className="flex items-center gap-2.5 border border-slate-200 rounded-md px-2.5 py-1.5 bg-slate-50 text-slate-500">
              <SpinnerGap size={16} className="animate-spin text-blue-500" />
              <div className="text-sm truncate flex-1">{n}</div>
              <span className="text-[10px] uppercase tracking-wide">Uploading…</span>
            </div>
          ))}
        </div>
      )}
      {error && <div className="text-xs text-rose-600">{error}</div>}
    </div>
  );
}

function AttachIcon({ att }) {
  if (isImageType(att.content_type)) {
    return <img src={att.url} alt={att.name} className="w-9 h-9 rounded object-cover border border-slate-200" />;
  }
  return (
    <div className="w-9 h-9 rounded bg-slate-100 text-slate-500 flex items-center justify-center">
      <FileText size={16} />
    </div>
  );
}

/** Read-only attachment gallery used on the ticket detail page. */
export function AttachmentList({ attachments }) {
  if (!attachments || attachments.length === 0) return null;
  const images = attachments.filter((a) => isImageType(a.content_type));
  const files = attachments.filter((a) => !isImageType(a.content_type));
  return (
    <div className="space-y-3" data-testid="attachment-list">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((a) => (
            <a key={a.path} href={a.url} target="_blank" rel="noreferrer" title={a.name}
              className="block w-24 h-24 rounded-md overflow-hidden border border-slate-200 hover:border-blue-400">
              <img src={a.url} alt={a.name} className="w-full h-full object-cover" />
            </a>
          ))}
        </div>
      )}
      {files.map((a) => (
        <a key={a.path} href={a.url} target="_blank" rel="noreferrer"
          className="flex items-center gap-2.5 border border-slate-200 rounded-md px-3 py-2 hover:border-blue-400 hover:bg-slate-50 group">
          <div className="w-8 h-8 rounded bg-slate-100 text-slate-500 flex items-center justify-center"><FileText size={15} /></div>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-slate-700 truncate">{a.name}</div>
            <div className="text-[10px] text-slate-400">{formatBytes(a.size)}</div>
          </div>
          <DownloadSimple size={15} className="text-slate-400 group-hover:text-blue-600" />
        </a>
      ))}
    </div>
  );
}
